import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useLang } from "@/App";
import { copy } from "@/i18n";

const PHOTOS = [
    { src: "/media/vetrina.png", alt: "Dove c'è tagliere... deve esserci bicchiere" },
    { src: "/media/patanegra.png", alt: "Pata Negra e il neon #themarketlugano" },
    { src: "/media/brindisi.png", alt: "Brindisi con i rossi della casa" },
    { src: "/media/locale-a.webp", alt: "Il banco con le spine in rame" },
    { src: "/media/cantina-vini.png", alt: "La cantina: Ca' del Bosco, Jacquesson, La Montina" },
    { src: "/media/dehors.webp", alt: "Il dehors la sera" },
    { src: "/media/poster-taste.png", alt: "Taste Share Repeat — taglieri The Market" },
    { src: "/media/claseazul.png", alt: "Clase Azul e un calice di bianco" },
    { src: "/media/piatti-taglieri.jpg", alt: "Taglieri e Franciacorta La Montina" },
    { src: "/media/entrata.png", alt: "L'ingresso di The Market, la sera" },
    { src: "/media/serata-dj.jpg", alt: "DJ set — le serate The Market" },
    { src: "/media/piatti-tartare.png", alt: "Tartare di manzo e spritz" },
    { src: "/media/neon.webp", alt: "Il neon #themarketlugano" },
    { src: "/media/piatti-pinse.webp", alt: "Le pinse" },
    { src: "/media/esterno.webp", alt: "The Market da fuori, la sera" },
    { src: "/media/serata-cards.jpg", alt: "Le pun card di The Market" },
    { src: "/media/veranda-rossa.png", alt: "La festa in veranda" },
    { src: "/media/beef-selection.jpg", alt: "Chef Beef Selection e Chassagne-Montrachet" },
    { src: "/media/dj-set.webp", alt: "Il DJ set al banco" },
    { src: "/media/pun-card.png", alt: "Il bere vincerà sempre sul male" },
    { src: "/media/vitello-tonnato.webp", alt: "Il vitello tonnato servito al tavolo" },
    { src: "/media/locale-b.jpg", alt: "La parete dei vini" },
    { src: "/media/vino-versato.webp", alt: "Il rosato versato al banco" },
    { src: "/media/tagliere-formaggi.webp", alt: "Il tagliere di formaggi" },
    { src: "/media/gin-collection.webp", alt: "La collezione di gin sul banco" },
    { src: "/media/cocktail-diary.webp", alt: "The Market Diary — not a secret, just good drinks" },
    { src: "/media/tagliere-miele.webp", alt: "Formaggi, uva e confettura" },
    { src: "/media/cortador-glove.webp", alt: "Il cortador al lavoro" },
    { src: "/media/gin-wall.webp", alt: "La parete dei gin" },
    { src: "/media/frigo-salumi.jpg", alt: "La vetrina dei salumi" },
    { src: "/media/team.webp", alt: "Il team di The Market" },
    { src: "/media/dj-rane.webp", alt: "Il DJ alla consolle in veranda" },
    { src: "/media/banco-sera.webp", alt: "Il banco la sera, tra Pata Negra e neon" },
    { src: "/media/poster-drinks.png", alt: "Come for drinks, stay for dinner" },
    { src: "/media/phone-pinsa.webp", alt: "Le pinse viste dai nostri ospiti" },
    { src: "/media/sala-rossa.png", alt: "La sala si accende di rosso" },
    { src: "/media/locale-c.webp", alt: "Il tavolo alto" },
    { src: "/media/taglieri-fila.jpg", alt: "Taglieri a volontà" },
    { src: "/media/phone-red.webp", alt: "Tortellini in diretta" },
    { src: "/media/tavolata-neon.jpg", alt: "La tavolata sotto il neon" },
    { src: "/media/sala-piena.jpg", alt: "Serata piena" },
];

const Gallery = () => {
    const { lang } = useLang();
    const c = copy[lang].gallery;
    const [selected, setSelected] = useState(null);

    return (
        <section id="gallery" data-testid="gallery-section" className="relative py-20 lg:py-32 bg-coal/40 border-y border-white/5">
            <div className="max-w-7xl mx-auto px-4 sm:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 28 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                >
                    <span className="text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-crimson">
                        {c.label}
                    </span>
                    <h2 className="mt-4 font-display font-extrabold uppercase tracking-tight text-2xl sm:text-4xl lg:text-5xl">
                        {c.title}
                    </h2>
                </motion.div>

                <div className="mt-12 columns-2 md:columns-3 gap-4 sm:gap-6">
                    {PHOTOS.map((p, i) => (
                        <motion.button
                            key={i}
                            data-testid={`gallery-item-${i}`}
                            initial={{ opacity: 0, y: 24 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-40px" }}
                            transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
                            onClick={() => setSelected(p)}
                            className="group relative w-full mb-4 sm:mb-6 overflow-hidden rounded-2xl border border-white/10 hover:border-brass/50 transition-colors break-inside-avoid"
                        >
                            <img
                                src={p.src}
                                alt={p.alt}
                                className="w-full h-auto block transition-transform duration-700 group-hover:scale-105"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-noir/15 group-hover:bg-noir/0 transition-colors" />
                        </motion.button>
                    ))}
                </div>
            </div>

            <AnimatePresence>
                {selected && (
                    <motion.div
                        data-testid="gallery-lightbox"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] bg-noir/95 backdrop-blur flex items-center justify-center p-4"
                        onClick={() => setSelected(null)}
                    >
                        <button
                            data-testid="lightbox-close"
                            className="absolute top-6 right-6 text-white hover:text-crimson-bright transition-colors p-2"
                            aria-label="Close"
                        >
                            <X size={28} />
                        </button>
                        <motion.img
                            initial={{ scale: 0.92 }}
                            animate={{ scale: 1 }}
                            src={selected.src}
                            alt={selected.alt}
                            className="max-w-full max-h-[85vh] rounded-2xl border border-white/10 object-contain"
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default Gallery;
